/**
 * Post-login return-to-page capture, open-redirect guard, and single-use
 * consume.
 *
 * A guest who leaves the page to sign in (the Google button outside
 * an auth-route pop-up) is captured here first; the login success surfaces
 * consume the captured path so the user lands back where they were instead
 * of on a generic default. Persisted in sessionStorage
 * under the key exported here (naming mirrors `utils/pkce.ts`'s
 * `OAUTH_STATE_STORAGE_KEY`) so the value survives the Cognito Hosted UI
 * round trip within the same tab.
 */
import type { MouseEvent } from 'react';

/** sessionStorage key holding the captured post-login return-to path. */
export const POST_LOGIN_REDIRECT_STORAGE_KEY = 'belhop_post_login_redirect';

/**
 * Auth-flow routes that must never be captured as a return-to destination —
 * capturing one of these would produce a redirect loop back into the auth
 * wall itself. Matched segment-aware (see isSafeRedirectPath), not as a
 * prefix, so a hypothetical route that merely starts with one of these
 * strings is not falsely excluded.
 */
const AUTH_FLOW_DENY_LIST_ROUTES = [
  '/login',
  '/auth/callback',
  '/register',
  '/verify-email',
  '/forgot-password',
];

/**
 * True when `route` matches `path` at a segment boundary: either `path`
 * equals `route` exactly, or `route` is immediately followed by `/`, `?`,
 * or `#` in `path`. This is what makes the deny-list reject `/login?next=1`
 * and `/login/x` while accepting `/loginsomething`.
 */
function matchesRouteSegment(path: string, route: string): boolean {
  if (path === route) {
    return true;
  }
  if (!path.startsWith(route)) {
    return false;
  }
  const nextCharacter = path.charAt(route.length);
  return nextCharacter === '/' || nextCharacter === '?' || nextCharacter === '#';
}

/** True when `pathname` is one of the auth-flow routes (the ADR-0044 deny-list). */
export function isAuthFlowPath(pathname: string): boolean {
  return AUTH_FLOW_DENY_LIST_ROUTES.some((route) => matchesRouteSegment(pathname, route));
}

/**
 * True when a click on a guest sign-in / sign-up link should open the pop-up:
 * a plain left click on a page that is not itself an auth-flow page. Modified
 * and non-primary clicks (open in a new tab) and auth pages keep the link.
 */
export function isPlainGuestLinkClick(event: MouseEvent, pathname: string): boolean {
  const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  return event.button === 0 && !isModifiedClick && !isAuthFlowPath(pathname);
}

/**
 * Guards against open-redirect vectors and auth-flow redirect loops. Only a
 * same-origin relative path (single leading `/`, not protocol-relative or
 * backslash-prefixed, and free of ASCII control characters) that is not one
 * of the deny-listed auth-flow routes is considered safe. Control characters
 * (tab, newline, carriage return, and the rest of the C0 range) are rejected
 * outright because browsers strip them from URLs during parsing — a path
 * such as `/\t/evil.com` looks same-origin here but normalizes to the
 * protocol-relative `//evil.com` once handed to the navigation APIs, which
 * would otherwise defeat this guard. A legitimate same-origin path never
 * contains a raw control character; real query/hash content is
 * percent-encoded. Called at both capture and consume time (defence in
 * depth) so a value can never be honoured without being validated on the
 * way out.
 */
export function isSafeRedirectPath(path: string): boolean {
  const trimmedPath = path.trim();

  // Rejecting the C0 control range plus DEL is the point of this check, not
  // an oversight, hence the inline suppression on the next line.
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(trimmedPath)) {
    return false;
  }

  if (!trimmedPath.startsWith('/')) {
    return false;
  }
  const secondCharacter = trimmedPath.charAt(1);
  if (secondCharacter === '/' || secondCharacter === '\\') {
    return false;
  }

  return !isAuthFlowPath(trimmedPath);
}

/**
 * Persists `path` as the post-login return-to destination, but only when it
 * passes isSafeRedirectPath — an unsafe path is silently dropped rather than
 * ever written to storage.
 */
export function capturePostLoginRedirect(path: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (!isSafeRedirectPath(path)) {
    return;
  }
  window.sessionStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, path);
}

/**
 * Captures the current location (pathname + search + hash) as the
 * post-login return-to destination. Because the deny-list rejects auth-flow
 * routes, this is a structural no-op when already on `/login` etc. — it
 * cannot produce a `/login`-captures-`/login` loop.
 */
export function captureCurrentLocation(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const currentPath = window.location.pathname + window.location.search + window.location.hash;
  capturePostLoginRedirect(currentPath);
}

/**
 * Reads and removes the captured post-login return-to path in one step —
 * removal happens on every call regardless of validity, which makes the
 * capture single-use and self-healing against a poisoned value written
 * directly into storage. Returns the path only if it re-validates against
 * isSafeRedirectPath; otherwise null.
 */
export function consumePostLoginRedirect(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const storedPath = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);

  if (storedPath === null || !isSafeRedirectPath(storedPath)) {
    return null;
  }
  return storedPath;
}
